import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Table2, Filter, Users } from 'lucide-react';
import StageSummaryReport from './StageSummaryReport';
import CandidateReport from './CandidateReport';
import { isPlainLeftClick } from './reportUtils';

const VIEWS = [
  {
    value: 'summary',
    label: 'Current stage',
    short: 'Current',
    icon: Table2,
    description: 'How many candidates are at each stage right now, job by job'
  },
  {
    value: 'funnel',
    label: 'Passed through',
    short: 'Passed through',
    icon: Filter,
    description: 'How many candidates have passed through each stage, job by job, including those who moved on or were rejected later'
  },
  {
    value: 'candidates',
    label: 'Candidate report',
    short: 'Candidates',
    icon: Users,
    description: 'Every candidate with their stage, status and interview progress'
  }
];

// The Reports tab: two job-by-stage reports (where candidates are now, and
// which stages they have passed through) and the candidate report both drill
// into. Which one shows, and the candidate report's filters, are owned by
// RecruitmentPortalApp and kept in the URL so any view can be refreshed,
// bookmarked or opened in a new tab.
const RecruitmentReports = ({ filters, onNavigate, getReportUrl, getCandidateUrl }) => {
  const { view, job, stage, reached, from, to, basis } = filters;
  const current = VIEWS.find((v) => v.value === view) || VIEWS[0];
  // Switching views keeps the period and the candidate report's last filters.
  const targetFor = (value) => ({ ...filters, view: value });
  // The period is shared by all three reports.
  const periodProps = {
    period: { from, to },
    basis,
    onPeriodChange: (nextFrom, nextTo) => onNavigate({ ...filters, from: nextFrom, to: nextTo }),
    onBasisChange: (nextBasis) => onNavigate({ ...filters, basis: nextBasis })
  };

  return (
    <div className="flex flex-col gap-6" data-testid="recruitment-reports">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-slate-900">Reports</h2>
          <p className="text-slate-500">{current.description}</p>
        </div>
        {/* Full width when stacked (scrolls sideways if a phone is too narrow);
            beside the heading it keeps its width and the text wraps instead. */}
        <div className="max-w-full overflow-x-auto lg:shrink-0">
          <Tabs value={view} onValueChange={(value) => onNavigate(targetFor(value))}>
            <TabsList className="w-max">
              {VIEWS.map(({ value, label, short, icon: Icon }) => (
                <TabsTrigger key={value} value={value} className="gap-2" asChild data-testid={`report-view-${value}`}>
                  <a
                    href={getReportUrl(targetFor(value))}
                    onClick={(e) => {
                      if (!isPlainLeftClick(e)) return;
                      e.preventDefault();
                      onNavigate(targetFor(value));
                    }}
                  >
                    {/* Phones get short labels and no icons, so all three fit. */}
                    <Icon className="hidden sm:block w-4 h-4" />
                    <span className="sm:hidden">{short}</span>
                    <span className="hidden sm:inline">{label}</span>
                  </a>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {view === 'candidates' ? (
        // A filter left out of `next` goes back to its default; the period stays.
        <CandidateReport
          jobFilter={job}
          stageFilter={stage}
          reachedFilter={reached}
          onFiltersChange={(next) => onNavigate({ view: 'candidates', from, to, basis, ...next })}
          getCandidateUrl={getCandidateUrl}
          {...periodProps}
        />
      ) : (
        // Keyed so switching between the two job-by-stage reports starts fresh.
        <StageSummaryReport
          key={view}
          mode={view === 'funnel' ? 'passed' : 'current'}
          getReportUrl={getReportUrl}
          onOpenReport={onNavigate}
          {...periodProps}
        />
      )}
    </div>
  );
};

export default RecruitmentReports;
